import React from "react";

import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import { PotentialDuplicateWarningDialog } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { matchSorter } from "match-sorter";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { renderHighlightedOption } from "@/components/alerts/add-v2/fields/highlightedOption";
import { useOccurrenceDuplicates } from "@/components/alerts/add-v2/hooks/useOccurrenceDuplicates";
import {
  useBulletinTypeOptions,
  useOperationalAreas,
  usePoliceServiceOptions,
} from "@/components/alerts/add-v2/hooks/useOptionLists";
import { normalizeOccurrenceNumbers } from "@/components/alerts/add-v2/model/occurrenceNumbers";
import {
  useBulletinField,
  useBulletinFieldError,
} from "@/components/alerts/add-v2/state/bulletinFormContext";
import { IPoliceService } from "@/types";

/**
 * Issuing police service, bulletin type, operational area, occurrence numbers — the prototype's
 * order, which puts the conditional field with the two selects it depends on rather than after the
 * chips.
 *
 * Sections take no props: values come from `useBulletinField`, option lists from their own hooks in
 * `hooks/`, and collapse/anchor/completion belong to the surrounding `FormSection`. See README
 * decision 14. Each `useBulletinField` subscribes to its own field, so typing in another section
 * does not re-render this one.
 *
 * All three autocompletes carry the production fields' configuration, not just their options — see
 * README decision 23 for what was missing and why one prop of it was left behind.
 */

/**
 * Fuzzy ranking rather than MUI's default substring filter, as in `DetailsStep`. It matters here and
 * nowhere else on this form: the list is every police service in the system, and officers type
 * fragments ("sudb", "gtr sudbury").
 */
const filterPoliceServices = (
  options: IPoliceService[],
  { inputValue }: { inputValue: string },
): IPoliceService[] => matchSorter(options, inputValue, { keys: ["name"] });

export const SetupSection: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const navigate = useNavigate();

  const policeServices = usePoliceServiceOptions();

  const [policeService, setPoliceService] = useBulletinField("policeService");
  const [bulletinType, setBulletinType] = useBulletinField("bulletinType");
  // `bulletinType` holds a `reference_name`, which is what the payload and the entity seed table both
  // key off; this list is the only thing that can name it. See `useBulletinTypeOptions`.
  const bulletinTypes = useBulletinTypeOptions();
  const [occurrenceNumbers, setOccurrenceNumbers] =
    useBulletinField("occurrenceNumbers");
  const [operationalArea, setOperationalArea] =
    useBulletinField("operationalArea");

  // The four autocompletes are not registered inputs, so they read their error rather than being
  // handed one — see `useBulletinFieldError`.
  const policeServiceError = useBulletinFieldError("policeService");
  const bulletinTypeError = useBulletinFieldError("bulletinType");
  const occurrenceError = useBulletinFieldError("occurrenceNumbers");
  const operationalAreaError = useBulletinFieldError("operationalArea");

  const operationalAreas = useOperationalAreas(
    policeService,
    policeServices.options,
  );
  const duplicates = useOccurrenceDuplicates();

  const changeOccurrenceNumbers = (next: string[]) => {
    const numbers = normalizeOccurrenceNumbers(next);

    // Nothing new came out of an addition, so the number typed was one already on this bulletin. The
    // old flow lets it through as a second identical chip; the design warns instead.
    if (
      next.length > occurrenceNumbers.length &&
      numbers.length === occurrenceNumbers.length
    ) {
      toast.info(t("fields.occurrence_already_added"));
      return;
    }

    setOccurrenceNumbers(numbers);
    numbers
      .filter((number) => !occurrenceNumbers.includes(number))
      .forEach(duplicates.check);
  };

  const changePoliceService = (next: IPoliceService | null) => {
    setPoliceService(next);

    // An operational area belongs to one service, so switching service always invalidates it — even
    // when the new service happens to offer an area of the same name, which would be a different area.
    // Re-picking the service already selected is not a switch and leaves the area alone.
    //
    // Deliberately done here and not in an effect watching `operationalAreas`: that list is empty
    // until the service list lands, so an effect would clear a perfectly good value on load.
    if (next?.id !== policeService?.id) setOperationalArea(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        }}
      >
        <Autocomplete<IPoliceService>
          options={policeServices.options}
          value={policeService}
          onChange={(_event, next) => changePoliceService(next)}
          getOptionLabel={(option) => option.name ?? ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={filterPoliceServices}
          autoComplete
          autoHighlight
          clearOnBlur
          openOnFocus
          loading={policeServices.loading}
          size="small"
          renderOption={(optionProps, option, { inputValue }) =>
            renderHighlightedOption(optionProps, option.name ?? "", inputValue)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              required
              error={!!policeServiceError}
              helperText={policeServiceError}
              label={t("fields.issuing_police_service")}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {policeServices.loading && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />

        <Autocomplete<string>
          options={bulletinTypes.options}
          value={bulletinType}
          onChange={(_event, next) => setBulletinType(next)}
          getOptionLabel={bulletinTypes.getOptionLabel}
          autoComplete
          autoHighlight
          clearOnBlur
          openOnFocus
          loading={bulletinTypes.loading}
          size="small"
          renderOption={(optionProps, option, { inputValue }) =>
            renderHighlightedOption(
              optionProps,
              bulletinTypes.getOptionLabel(option),
              inputValue,
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              required
              error={!!bulletinTypeError}
              helperText={bulletinTypeError}
              label={t("fields.bulletin_type")}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {bulletinTypes.loading && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Box>

      {/*
       * Shown only when the selected police service defines operational areas, and required when it
       * does — the same rule the old flow applies, and the one `validation/schemas.ts` counts
       * against. It is a closed list, not free text: the options are the service's own
       * `rubi_org.operational_areas`.
       */}
      {operationalAreas.length > 0 && (
        <Autocomplete<string>
          options={operationalAreas}
          value={operationalArea}
          onChange={(_event, next) => setOperationalArea(next)}
          autoComplete
          autoHighlight
          clearOnBlur
          openOnFocus
          size="small"
          fullWidth
          renderOption={(optionProps, option, { inputValue }) =>
            renderHighlightedOption(optionProps, option, inputValue)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              required
              error={!!operationalAreaError}
              helperText={operationalAreaError}
              label={t("fields.operational_area")}
            />
          )}
        />
      )}

      <Autocomplete<string, true, false, true>
        multiple
        freeSolo
        autoSelect
        options={[]}
        value={occurrenceNumbers}
        onChange={(_event, next) => changeOccurrenceNumbers(next)}
        size="small"
        renderTags={(values, getTagProps) =>
          values.map((value, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} variant="outlined" label={value} {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            required
            error={!!occurrenceError}
            label={t("fields.occurrence_numbers")}
            helperText={occurrenceError ?? t("fields.occurrence_hint")}
          />
        )}
      />

      {/*
       * `onCancel` dismisses the warning and navigates to the alerts list. It does not delete
       * anything, and it cannot claim there is nothing to delete: autosave may already have
       * persisted this draft by the time the duplicate check returns. So leaving here can strand
       * a draft row, and the fix belongs on this handler — it should route through the
       * repository's discard, the same one the page's discard action uses.
       */}
      <PotentialDuplicateWarningDialog
        open={duplicates.matches.length > 0}
        title={t("duplicate.title")}
        message={t("duplicate.message")}
        items={duplicates.matches.map((match) => ({
          id: String(match.id),
          href: `/rubialert/alerts/${match.id}/single`,
          label: match.occurrenceNumbers.join(", "),
        }))}
        cancelLabel={t("duplicate.cancel")}
        continueLabel={t("duplicate.continue")}
        onCancel={() => {
          duplicates.dismiss();
          navigate("/rubialert/alerts");
        }}
        onContinue={duplicates.dismiss}
      />
    </Box>
  );
};
